/**
 * MobileWorkspaceScreen — the real workspace surface on a phone
 * (docs/plans/2026-07-07_MOBILE_WORKSPACE_SURFACE.md). Desktop shows the
 * three panes side by side (WorkspaceView); a phone shows ONE at a time —
 * Files (the same WorkspaceExplorer tree as desktop) / Editor (desktop
 * TabBar + TabContent stack) / Assistant (ChatPanel) — switched by a
 * bottom section bar. All three stay mounted (display-toggled, the same
 * trick WorkspaceView uses for inactive tabs) so editor and chat state
 * survive switching; chat lazy-mounts on first visit like desktop's
 * chatMounted.
 *
 * No chrome above the sections: the workspace's identity lives in the
 * MobileHeader brand label ("WORKSPACE / <name>"), which opens the
 * workspace switcher (MobileWorkspaceSwitcher) — that's also how you
 * leave for another workspace or the list.
 *
 * State comes from the SAME useWorkspace hook desktop uses — tabs, links,
 * permissions, config persistence all behave identically; only the layout
 * is mobile. No WorkspaceManagerProvider here: the URL is the single open
 * workspace (`:workspaceId` is the .workpapan FOLDER id, as on desktop).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { revealLineRequest, type RevealLineRequest } from '@papan/collab/client'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Code, FileInput, FolderTree, MessageCircle, Save, Sparkles } from 'lucide-react'
import {
  CommandRegistryProvider,
  ErrorBoundary,
  ThemedArea,
  FileContextMenu,
  useContextMenu,
  useToast,
  DraftScopeProvider,
  WORKSPACE_SCOPE_ATTR,
  WORKSPACE_LOADED_ATTR,
  type FileMenuItem,
} from '@papan/core-ui/ui'
import {
  useWorkspace,
  PLAIN_FOLDER_CONFIG_ERROR,
  WorkspaceExplorer,
  TabContent,
  ChatPanel,
  CompactTabSwitcher,
  TabListSheet,
  useTabPillAnchor,
  type WorkspaceExplorerHandle,
  type Tab,
} from '@papan/workspace/ui'
import { filesApi } from '@papan/files/client'
import { workspaceApi, loadWorkspaceAccess } from '@papan/workspace/client'
import { request } from '@papan/api-client'
import { enforcedAIOverride, canWriteFor, createAccessBurst, changesViewerAccess, type WorkspaceAccessOutcome } from '@papan/types'
import { useRealtime } from '@papan/realtime/client'
import { orgsApi } from '@papan/orgs/client'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'

type Section = 'files' | 'editor'

export function MobileWorkspaceScreen() {
  const { workspaceId: folderId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const { user } = useAuth()

  const ws = useWorkspace({ folderId: folderId ?? null, isActive: true })
  const queryClient = useQueryClient()
  const toast = useToast()

  // Entering a workspace REGISTERS it as open (desktop's auto-register
  // parity) — that's what feeds the switcher's "Active workspaces" section.
  useEffect(() => {
    if (!ws.workspaceId) return
    workspaceApi.markOpen(ws.workspaceId).catch(() => {})
    void queryClient.invalidateQueries({ queryKey: ['mobile-open-workspaces'] })
  }, [ws.workspaceId, queryClient])

  // Land on the EDITOR (the workspace's main surface — restored tabs show
  // immediately); Files/Assistant are one tap away on the section bar.
  const [section, setSection] = useState<Section>('editor')
  // Chat mounts on first open and never unmounts (streams/history survive) —
  // same lifecycle as WorkspaceView's chatMounted. The assistant is a
  // slide-in overlay (chatOpen), not a section.
  const [chatMounted, setChatMounted] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  // The overlay header's right-hand slot: ChatPanel portals its export +
  // budget into here (callback ref → state so ChatPanel re-renders once the
  // node attaches). See the assistant overlay below.
  const [chatHeaderSlot, setChatHeaderSlot] = useState<HTMLDivElement | null>(null)
  // Opening the assistant also closes the explorer drawer (return to editor
  // underneath, so closing the chat lands back on the editor, not the tree).
  const openChat = () => { setSection('editor'); setChatMounted(true); setChatOpen(true) }
  const showSection = (s: Section) => setSection(s)
  // The side icons TOGGLE: tapping Files/AI again closes that section back
  // to the editor.
  const toggleSection = (s: Section) => showSection(section === s ? 'editor' : s)
  // Full-screen tab overview — opened by BOTH the list button AND tapping the
  // active-tab pill (same function). Its search state lives in TabListSheet,
  // which unmounts on close, so every opening starts fresh.
  const [tabsListOpen, setTabsListOpen] = useState(false)
  const toggleTabList = () => setTabsListOpen((o) => !o)
  // Tapping the active-tab pill: when the explorer is open it ONLY returns to
  // the editor (does not open the tab list); in the editor view it toggles
  // the tab list as before.
  const handleActiveTabTap = () => { if (section === 'files') { setSection('editor'); return } toggleTabList() }
  // The list button always shows the tab list, closing the explorer first.
  const showTabList = () => { setSection('editor'); toggleTabList() }

  // The list rows mirror the opened-tab pill's box (see useTabPillAnchor).
  const { pillWrapRef, anchor: pillAnchor } = useTabPillAnchor(tabsListOpen)
  // ⋮ menu on a tab pill — same shared FileContextMenu as desktop's TabBar.
  // ignoreTarget: the ⋮ triggers own their own toggle, so the outside-click
  // mousedown handler must NOT close on them (it fires before the button's
  // onClick and would otherwise close-then-reopen, defeating the toggle).
  const tabMenu = useContextMenu<{ x: number; y: number; tabId: string }>({
    ignoreTarget: (t) => !!(t as HTMLElement | null)?.closest?.('[data-tabmenu-trigger]'),
  })
  // Toggle: tapping the ⋮ of the tab whose menu is already open closes it.
  const toggleTabMenu = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    if (tabMenu.menu?.tabId === tabId) { tabMenu.close(); return }
    tabMenu.open({ x: e.clientX, y: e.clientY + 8, tabId })
  }

  // Org role for the active email — same source as the launcher/header
  // (cache key shared, so this is free when they've fetched already).
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  const tenant = (tenantsQuery.data?.tenants ?? []).find((t) => t.id === tenantId)
  const email = activeOrgEmail ?? user?.email
  const userRole = tenant && tenant.type !== 'personal'
    ? (tenant.emails.find((e) => e.email === email)?.role ?? tenant.emails[0]?.role)
    : undefined

  // Effective permissions — same derivation as WorkspaceView: the permission resolved on the
  // workspace folder alone, never the org role (#872); unknown (config still loading) allows nothing.
  // Live: an access / role change re-reads it (below), as the desktop WorkspaceView does.
  // Keyed by the workspace it answers for: the screen stays mounted across workspaces, so an
  // answer for the previous one must never apply to the next (not even for one render).
  const [liveWsPerm, setLiveWsPerm] = useState<{ folderId: string; perm: string } | null>(null)
  const wsPerm = (liveWsPerm && liveWsPerm.folderId === folderId ? liveWsPerm.perm : null) ?? ws.callerPermission
  const canWrite = canWriteFor(wsPerm)
  const canCopy = !!wsPerm && ['admin', 'readwrite', 'readcopy', 'owner'].includes(wsPerm)

  // Per-folder permission map for the explorer (role-based overlaid with
  // user-specific grants) — mirrors WorkspaceView's fetchUserPermMap.
  const [userPermMap, setUserPermMap] = useState<Map<string, string>>(new Map())
  const fetchUserPermMap = useCallback(() => {
    request<{ permissions: Array<{ folderId: string; permission: string }>; rolePermissions: Array<{ folderId: string; permission: string }> }>(
      '/api/folders/user/me/permissions'
    ).then((data) => {
      const map = new Map<string, string>()
      for (const p of data.rolePermissions) map.set(p.folderId, p.permission)
      for (const p of data.permissions) map.set(p.folderId, p.permission)
      setUserPermMap(map)
    }).catch(() => {})
  }, [])
  useEffect(() => { fetchUserPermMap() }, [userRole, fetchUserPermMap])

  // #1406 I3: a project refused to this person that their drive still opens (a hallway project, or
  // one whose config they can't read) is a plain folder to them — whichever button brought them
  // here (Edit PDApp, an app's .project redirect), browse it as the drive does.
  useEffect(() => {
    if (folderId && ws.configError === PLAIN_FOLDER_CONFIG_ERROR) {
      navigate(`${isPersonal ? '/~' : `/t/${tenantId}`}/folder/${folderId}`, { replace: true })
    }
  }, [ws.configError, folderId, navigate, isPersonal, tenantId])

  // Live access re-check (I262; as WorkspaceView): an org access / role change re-reads the
  // permission map and the workspace folder's own permission — a revoke leaves the workspace, a
  // demotion turns it read-only — at most once per interval (`createAccessBurst`), the folder's
  // permission read together with every other one asked at that moment (`loadPermission`, #1259).
  const { onOrgEvent, onPermissionChanged } = useRealtime()
  useEffect(() => {
    if (!folderId) return
    // This screen stays mounted across workspaces (only `:workspaceId` changes), so an answer
    // still in flight for the PREVIOUS workspace must not land on the next one.
    let live = true
    const leave = () => { if (live) navigate(`${isPersonal ? '/~' : `/t/${tenantId}`}/folder`) }
    // A permission:changed naming no one (a link minted — every member gets those) re-checks only
    // the folder; the permission map is re-read for this viewer's own change (as WorkspaceView).
    let full = false
    const burst = createAccessBurst(() => {
      if (full) { full = false; fetchUserPermMap() }
      const apply = (outcome: WorkspaceAccessOutcome) => {
        if (!live) return
        if (outcome.kind === 'leave') leave()
        else if (outcome.kind === 'permission') setLiveWsPerm({ folderId, perm: outcome.permission })
      }
      // as of NOW: an answer asked before a later change is superseded (`null`) — that change's own
      // reaction asks again (#1260, Codex review: a 403 read before a re-grant closed the workspace)
      // …and (#1406) whether it still opens as a project — the ONE re-check (`loadWorkspaceAccess`)
      loadWorkspaceAccess(folderId).then((outcome) => { if (outcome) apply(outcome) })
    })
    const off = onOrgEvent((event) => {
      // #1259: a change naming another member leaves this viewer's access as it was
      if ((event.type === 'org:access_changed' || event.type === 'org:role_changed') && changesViewerAccess(event)) { full = true; burst.push() }
    })
    // …and permission:changed — some changes publish only that (as WorkspaceView)
    const offPerm = onPermissionChanged((event) => {
      if (!changesViewerAccess(event)) return
      if (event.affectsViewer === true) full = true
      burst.push()
    })
    return () => { live = false; setLiveWsPerm(null); off(); offPerm(); burst.cancel() }
  }, [onOrgEvent, onPermissionChanged, folderId, fetchUserPermMap, navigate, isPersonal, tenantId])

  const explorerRef = useRef<WorkspaceExplorerHandle>(null)

  // Per-tab save/discard triggers (TabContent exposes them via refs,
  // desktop-style) — consumed by the TabBar menu.
  /** #930 — tabs whose editor covers itself (the conflict "Saved version" panel); see EditorCover. */
  const [coveredTabs, setCoveredTabs] = useState<ReadonlySet<string>>(() => new Set())
  const saveRefs = useRef(new Map<string, MutableRefObject<(() => void) | null>>())
  const discardRefs = useRef(new Map<string, MutableRefObject<(() => void) | null>>())
  const getRef = (map: Map<string, MutableRefObject<(() => void) | null>>, tabId: string) => {
    let r = map.get(tabId)
    if (!r) {
      r = { current: null }
      map.set(tabId, r)
    }
    return r
  }

  // Opening a file jumps to the Editor section — the mobile equivalent of
  // the file appearing in the pane next door on desktop.
  // #249: the line a chat link (`app.py:42`) asked for, kept with the file it is for.
  const [reveal, setReveal] = useState<{ fileId: string; req: RevealLineRequest } | null>(null)
  const openFile = useCallback((fileId: string, name?: string) => {
    setReveal(null) // handleOpenPath sets its own line after this
    void ws.openFile(fileId, name ? { name } : undefined)
    setSection('editor')
    setChatOpen(false) // opening a file (incl. from the assistant) shows the editor
  }, [ws.openFile]) // eslint-disable-line react-hooks/exhaustive-deps

  // Path-based opens (assistant citations, sandboxed HTML `papan:openFile`).
  const handleOpenPath = useCallback(async (path: string, at?: { line?: number }) => {
    try {
      const result = await filesApi.resolvePath(path)
      if (!result?.id) {
        toast.error(`File not found: ${path}`)
        return
      }
      if (result.file.isFolder) {
        explorerRef.current?.revealFile(result.id)
        setSection('files')
        setChatOpen(false) // reveal in the explorer behind the assistant
      } else {
        openFile(result.id)
        if (at?.line && at.line > 0) setReveal({ fileId: result.id, req: revealLineRequest(at.line) })
      }
    } catch {
      toast.error(`Could not open: ${path}`)
    }
  }, [openFile, toast])

  const handleFileDeleted = useCallback((fileId: string, opts: { isFolder: boolean; path?: string }) => {
    for (const t of ws.tabs) {
      if (t.fileId === fileId || (opts.isFolder && opts.path && t.path.startsWith(opts.path + '/'))) {
        ws.closeTab(t.id)
      }
    }
  }, [ws.tabs, ws.closeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // Assistant context — active tab unless AI access is overridden to hidden
  // (same rule as WorkspaceView's activeTabInfo).
  const activeTabInfo = useMemo(() => {
    if (!ws.activeTab) return null
    if (enforcedAIOverride(ws.aiOverrideMap?.get(ws.activeTab.fileId)) === 'hidden') return null
    return { fileId: ws.activeTab.fileId, fileName: ws.activeTab.title, filePath: ws.activeTab.path, fileType: ws.activeTab.type }
  }, [ws.activeTab, ws.aiOverrideMap])

  // First-seen order of tab ids for the editor-pane stack (see `paneTabs` below).
  // The tab list's drag-to-reorder lives in TabListSheet and calls
  // ws.reorderTabs exactly once, on drop — so this order is never disturbed.
  const paneOrderRef = useRef<string[]>([])

  // Editor panes render in a STABLE first-seen order, DECOUPLED from the tab-list
  // order. Only one pane is ever visible (display toggled by activeTabId), so the
  // DOM order is invisible — but it MUST stay put: if the panes reordered to match
  // a tab drag, React would move each pane's <div> (and its preview <iframe>) in
  // the DOM, and moving an iframe reloads it — the "background keeps changing /
  // sandbox reopens" bug. New tabs append; closed tabs drop; reordering never
  // touches this. (The ref is mutated during render on purpose — a persisted
  // derived order, same shape as a useMemo with a running accumulator.)
  const panes = paneOrderRef.current.filter((id) => ws.tabs.some((t) => t.id === id))
  for (const t of ws.tabs) if (!panes.includes(t.id)) panes.push(t.id)
  paneOrderRef.current = panes
  const paneTabs = panes
    .map((id) => ws.tabs.find((t) => t.id === id))
    .filter((t): t is Tab => !!t)

  if (!folderId) return null

  return (
    <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0">
      <CommandRegistryProvider>
        <div className="relative flex-1 flex min-w-0 min-h-0 flex-col">
          {/* Control row — the workspace's single navigation strip (replaces
              both the tab strip and the old bottom section bar):
              [Files] · [active-tab pill, centered, max-width] · [list button] · [AI].
              Files/AI switch sections; tapping the pill returns to the
              editor; the list button (the boxed tab count) drops a vertical
              list of ALL tabs (one row per tab, same width as the pill). */}
          <div className="relative flex shrink-0 items-center gap-1 px-2 pt-1.5 pb-3">
            <button
              onClick={() => toggleSection('files')}
              aria-label="File explorer"
              aria-current={section === 'files' ? 'true' : undefined}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg active:themed-bg-tertiary ${
                section === 'files' ? 'text-blue-500' : 'themed-icon'
              }`}
            >
              <FolderTree size={19} />
            </button>

            {/* Tapping the pill opens the tab list (same as the list button)
                when in the editor; when the explorer is open it just returns
                to the editor. The ⋮ still acts on this tab. */}
            <CompactTabSwitcher
              activeTab={ws.activeTab ?? null}
              tabCount={ws.tabs.length}
              listOpen={tabsListOpen}
              highlighted={section === 'editor'}
              pillWrapRef={pillWrapRef}
              onPillTap={handleActiveTabTap}
              onShowList={showTabList}
              onOpenMenu={toggleTabMenu}
              onEmptyTap={() => showSection('editor')}
            />

            <button
              onClick={openChat}
              aria-label="AI assistant"
              aria-expanded={chatOpen}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg active:themed-bg-tertiary ${
                chatOpen ? 'text-blue-500' : 'themed-icon'
              }`}
            >
              <MessageCircle size={19} />
            </button>

          </div>

          {/* Sections — all mounted, one visible (state survives switching) */}
          <div className="relative flex-1 min-h-0">
            {/* Editor — the TabContent stack (the tab strip lives in the
                control row above), in the same rounded ws-card as the chat.
                Always the BASE layer: the explorer + chat overlays slide over
                it, and the explorer's 10% peek shows it behind. */}
            <div className="absolute inset-0 flex flex-col">
              <div className="ws-card flex-1 flex min-h-0 flex-col" {...{ [WORKSPACE_SCOPE_ATTR]: ws.workspaceId ?? undefined, [WORKSPACE_LOADED_ATTR]: ws.configLoaded ? '1' : undefined }}>
              {paneTabs.map((tab) => (
                <div
                  key={tab.id}
                  className="flex-1 flex flex-col"
                  style={{ display: tab.id === ws.activeTabId ? undefined : 'none', minHeight: 0 }}
                >
                  <ErrorBoundary
                    fallback={
                      <div className="flex-1 flex items-center justify-center themed-text-muted">
                        <div className="text-center">
                          <p className="themed-text mb-2">Editor crashed</p>
                          <button onClick={() => ws.closeTab(tab.id)} className="text-xs themed-btn-secondary px-3 py-1 rounded">
                            Close tab
                          </button>
                        </div>
                      </div>
                    }
                  >
                    {/* #897 — same scope as the desktop workspace (the folder id
                        is the URL's), so an editor's unsaved draft crosses the swap. */}
                    <DraftScopeProvider scope={folderId ? `ws:${folderId}` : null}>
                    <TabContent
                      tab={tab}
                      tenantId={tenantId}
                      isActive={tab.id === ws.activeTabId}
                      revealLine={reveal?.fileId === tab.fileId ? reveal.req : undefined}
                      onDirtyChange={(dirty) => ws.setTabDirty(tab.id, dirty)}
                      onConflictChange={(c) => ws.setTabConflicted(tab.id, c)}
                      onCoverChange={(cover) => setCoveredTabs((prev) => {
                        if (cover ? prev.has(tab.id) : !prev.has(tab.id)) return prev
                        const next = new Set(prev)
                        if (cover) next.add(tab.id); else next.delete(tab.id)
                        return next
                      })}
                      saveRef={getRef(saveRefs.current, tab.id)}
                      discardRef={getRef(discardRefs.current, tab.id)}
                      onOpenFile={handleOpenPath}
                      onCloseTab={() => ws.closeTab(tab.id)}
                    />
                    </DraftScopeProvider>
                  </ErrorBoundary>
                </div>
              ))}
              {ws.tabs.length === 0 && (
                <div className="flex-1 flex items-center justify-center themed-text-muted">
                  <div className="text-center px-6">
                    <Code size={44} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No file open</p>
                    <p className="mt-1.5 text-xs opacity-60">Pick a file from the Files section below</p>
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Tab overview — a frosted overlay of the SECTIONS AREA ONLY.
                It lives INSIDE the sections container, so the control row
                above (the tab pill + list / Files / AI icons) stays visible
                and interactive while it's open. Opened by the pill OR the
                list button; the ⋮ menu (FileContextMenu) portals to body
                above everything. Shared with the narrow desktop TabBar. */}
            {tabsListOpen && (
              <TabListSheet
                tabs={ws.tabs}
                activeTabId={ws.activeTabId}
                anchor={pillAnchor}
                onSelect={(tabId) => { ws.setActiveTabId(tabId); setTabsListOpen(false); showSection('editor') }}
                onClose={ws.closeTab}
                onReorder={ws.reorderTabs}
                onOpenMenu={toggleTabMenu}
                onDismiss={() => setTabsListOpen(false)}
              />
            )}

          {/* Files explorer — a LEFT slide-in drawer INSIDE the sections area,
              so it does NOT cover the control row (Files icon + tab pill stay
              visible above). 75% width; the 25% peek on the right is a FROSTED
              fog (same blur as the tab-list overlay) over the editor — tapping
              it slides the drawer back. Always mounted so the tree's
              expand/scroll state survives. */}
          <div className={`absolute inset-0 z-30 ${section === 'files' ? '' : 'pointer-events-none'}`}>
            {section === 'files' && (
              <div
                className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0"
                onClick={() => setSection('editor')}
                aria-hidden
              />
            )}
            <div className={`absolute inset-y-0 left-0 flex w-[75%] flex-col transition-transform duration-300 ease-out ${section === 'files' ? 'translate-x-0' : '-translate-x-full'}`}>
              {/* No solid backing: the card's rounded RIGHT corners reveal the
                  fog blur behind (not a dark panel); the LEFT corners are
                  squared (flush to the screen edge). Card body ~93% opaque so
                  the tree stays readable over the blur. */}
              <div className="ws-card flex-1 flex min-h-0 flex-col" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                <WorkspaceExplorer
                  ref={explorerRef}
                  tenantId={tenantId}
                  tenantName={tenant?.name}
                  folderId={folderId}
                  links={ws.links}
                  activeFileId={ws.activeTab?.fileId ?? null}
                  aiOverrideMap={ws.aiOverrideMap}
                  canWrite={canWrite}
                  canCopy={canCopy}
                  userPermMap={userPermMap}
                  onFileOpen={openFile}
                  onAddLink={ws.addLink}
                  onRemoveLink={ws.removeLink}
                  onFileDeleted={handleFileDeleted}
                  onEnsureWorkspace={ws.ensureWorkspace}
                  onSetAIAccess={ws.setAIAccess}
                  onClearAIAccess={ws.clearAIAccess}
                />
              </div>
            </div>
          </div>
          </div>

          {/* AI assistant — slides in from the RIGHT and covers the whole
              workspace area (control row + sections), stopping BELOW the app
              header (this overlay lives inside the screen root, which sits
              under MobileHeader). Solid themed bg so nothing shows through;
              the back arrow slides it away. Mounted once (chatMounted) so the
              conversation survives closing/reopening. */}
          <div
            className={`absolute inset-0 z-40 flex flex-col themed-bg-primary transition-transform duration-300 ease-out ${
              chatOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
            }`}
            aria-hidden={!chatOpen}
            // `inert` is load-bearing, not decoration. The panel stays MOUNTED
            // when closed (to keep chat state), so `aria-hidden` +
            // pointer-events-none hide it from the a11y tree and the mouse — but
            // its inputs remain in the TAB ORDER. A keyboard or screen-reader
            // user tabbing the workspace landed on off-screen chat inputs they
            // couldn't see. `inert` is the one thing that removes a subtree from
            // focus as well as from hit-testing.
            inert={!chatOpen}
          >
            <div className="relative flex shrink-0 items-center px-2 py-2">
              <button
                onClick={() => setChatOpen(false)}
                aria-label="Close assistant"
                className="relative z-10 flex h-9 w-9 items-center justify-center rounded-lg themed-icon active:themed-bg-tertiary"
              >
                <FileInput size={20} />
              </button>
              {/* Centered title — absolute so the left back-button and the
                  right-hand export/budget slot don't shove it off-centre;
                  pointer-events-none so taps still reach both. */}
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 text-sm font-medium themed-text">
                <Sparkles size={16} className="text-blue-500" />
                AI Assistant
              </span>
              {/* Export + budget lift OUT of the ChatPanel and portal into
                  this slot (see ChatPanel's mobileHeaderSlot). */}
              <div ref={setChatHeaderSlot} className="relative z-10 ml-auto flex items-center gap-1" />
            </div>
            <div className="min-h-0 flex-1">
              {chatMounted && (
                <ChatPanel
                  workspaceId={ws.workspaceId ?? undefined}
                  workspaceFolderId={ws.folderId}
                  links={ws.configLoaded ? ws.links : undefined}
                  canWrite={canWrite}
                  activeTab={activeTabInfo}
                  orgEmail={activeOrgEmail}
                  onOpenFile={handleOpenPath}
                  onOpenBilling={() => navigate(`${isPersonal ? '/~' : `/t/${tenantId}`}/settings/subscription`)}
                  onManageChatStorage={() => navigate(`${isPersonal ? '/~' : `/t/${tenantId}`}/settings/storage`)}
                  mobile
                  mobileHeaderSlot={chatHeaderSlot}
                />
              )}
            </div>
          </div>

          {/* ⋮ menu for a tab pill — trimmed desktop tab menu. */}
          {tabMenu.menu && (() => {
            const tab = ws.tabs.find((t) => t.id === tabMenu.menu!.tabId)
            if (!tab) return null
            const hasDirty = ws.tabs.some((t) => t.isDirty && !coveredTabs.has(t.id))   // Save All skips covered tabs
            const items: FileMenuItem[] = [
              // #930 — a tab whose editor covers itself (the conflict "Saved version" panel) is never saved or discarded
              // from here (that writes / throws away its hidden live document); Save All skips it.
              { kind: 'item' as const, icon: Save, label: 'Save', disabled: !tab.isDirty || coveredTabs.has(tab.id), onClick: () => { saveRefs.current.get(tab.id)?.current?.(); tabMenu.close() } },
              { kind: 'item' as const, icon: Save, label: 'Save All', disabled: !hasDirty, onClick: () => { ws.tabs.forEach((t) => { if (t.isDirty && !coveredTabs.has(t.id)) saveRefs.current.get(t.id)?.current?.() }); tabMenu.close() } },
              { kind: 'item' as const, label: 'Discard Changes', danger: true, disabled: !tab.isDirty || coveredTabs.has(tab.id), onClick: () => { discardRefs.current.get(tab.id)?.current?.(); tabMenu.close() } },
              { kind: 'separator' as const },
              { kind: 'item' as const, label: 'Close', onClick: () => { ws.closeTab(tab.id); tabMenu.close() } },
              { kind: 'item' as const, label: 'Close Others', disabled: ws.tabs.length <= 1, onClick: () => { ws.closeOtherTabs(tab.id); tabMenu.close() } },
              { kind: 'item' as const, label: 'Close All', onClick: () => { ws.closeAllTabs(); tabMenu.close() } },
            ]
            return <FileContextMenu x={tabMenu.menu.x} y={tabMenu.menu.y} items={items} menuRef={tabMenu.menuRef} />
          })()}
        </div>
      </CommandRegistryProvider>
    </ThemedArea>
  )
}
